import { Chip } from "@legal-500/legal500-design-system";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import EmptyState from "../../../components/EmptyState/index.js";
import LoadingState from "../../../components/LoadingState/index.js";
import { useQuery } from "../../../hooks/useApi.js";
import {
  platformService,
  type NotificationStreamEntry,
  type NotificationStreamInfo,
  type NotificationStreamConsumerGroup,
  type MultiStreamSearchResponse,
} from "../../../api/platform.service.js";
import styles from "./Notifications.module.css";

function formatTimestamp(timestamp: number): string {
  return new Date(timestamp).toISOString().replace("T", " ").replace("Z", "");
}

function formatIdleTime(idleMs: number): string {
  if (idleMs < 1000) return `${idleMs}ms`;
  if (idleMs < 60000) return `${Math.round(idleMs / 1000)}s`;
  if (idleMs < 3600000) return `${Math.round(idleMs / 60000)}m`;
  return `${Math.round(idleMs / 3600000)}h`;
}

function matchesFilter(streamType: string, filter: string): boolean {
  if (!filter) return true;
  const pattern = filter.replace(/[.+^${}()|[\]\\]/g, "\\$&").replace(/\*/g, ".*");
  return new RegExp(`^${pattern}$`, "i").test(streamType);
}

function NotificationEventLog() {
  const [selectedStream, setSelectedStream] = useState<string | null>(null);
  const [streamFilter, setStreamFilter] = useState("");
  const [expandedEntryId, setExpandedEntryId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<MultiStreamSearchResponse | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    data: streamsData,
    loading: streamsLoading,
    error: streamsError,
  } = useQuery(() => platformService.getNotificationStreams(), []);

  const filteredStreams = useMemo(() => {
    const streams = streamsData?.streams ?? [];
    if (!streamFilter) return streams;
    return streams.filter(stream => matchesFilter(stream.type, streamFilter));
  }, [streamsData, streamFilter]);

  const activeFilter = selectedStream ?? (streamFilter || null);
  const hasActiveFilter = activeFilter !== null && filteredStreams.length > 0;

  const {
    data: entriesData,
    loading: entriesLoading,
    error: entriesError,
  } = useQuery(
    () =>
      hasActiveFilter && activeFilter
        ? selectedStream
          ? platformService.getNotificationStreamEntries(selectedStream, { count: 50 }).then(r => ({
              entries: r.entries,
              streamTypes: [selectedStream],
              requestId: r.requestId,
            }))
          : platformService.getMultiStreamEntries(activeFilter, { count: 50 })
        : Promise.resolve({ entries: [], streamTypes: [], requestId: "" }),
    [activeFilter, selectedStream],
  );

  const { data: consumersData, loading: consumersLoading } = useQuery(
    () =>
      selectedStream
        ? platformService.getNotificationStreamConsumers(selectedStream)
        : Promise.resolve({ groups: [], requestId: "" }),
    [selectedStream],
  );

  useEffect(() => {
    if (!hasActiveFilter || !activeFilter || !searchQuery.trim()) {
      setSearchResults(null);
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const searchFilter = selectedStream ?? streamFilter;

    debounceTimerRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        if (selectedStream) {
          const result = await platformService.searchNotificationStreamEntries(
            selectedStream,
            searchQuery.trim(),
            { limit: 50 },
          );
          setSearchResults({ ...result, streamTypes: [selectedStream] });
        } else {
          const result = await platformService.searchMultiStream(searchFilter, searchQuery.trim(), {
            limit: 50,
          });
          setSearchResults(result);
        }
      } finally {
        setSearchLoading(false);
      }
    }, 400);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [hasActiveFilter, activeFilter, selectedStream, streamFilter, searchQuery]);

  const handleStreamSelect = useCallback((type: string) => {
    setSelectedStream(prev => (prev === type ? null : type));
    setExpandedEntryId(null);
    setSearchQuery("");
    setSearchResults(null);
  }, []);

  const handleFilterChange = useCallback((value: string) => {
    setStreamFilter(value);
    setSelectedStream(null);
    setExpandedEntryId(null);
    setSearchQuery("");
    setSearchResults(null);
  }, []);

  if (streamsLoading) return <LoadingState message="Loading notification streams..." />;

  if (streamsError) {
    return (
      <EmptyState variant="error" title="Failed to load streams" message={streamsError.message} />
    );
  }

  if (!streamsData?.streams?.length) {
    return (
      <EmptyState
        variant="empty"
        title="No notification streams"
        message="No notification events have been published yet."
      />
    );
  }

  const displayedEntries = searchQuery
    ? (searchResults?.entries ?? [])
    : (entriesData?.entries ?? []);

  const showEntries = searchQuery
    ? !searchLoading && searchResults !== null && searchResults.entries.length > 0
    : !entriesLoading && displayedEntries.length > 0;

  return (
    <div className={styles.eventLogWrapper}>
      <div className={styles.streamFilterRow}>
        <label className={styles.streamSelectorLabel} htmlFor="stream-filter">
          Filter
        </label>
        <input
          id="stream-filter"
          type="text"
          className={styles.streamFilterInput}
          value={streamFilter}
          onChange={e => handleFilterChange(e.target.value)}
          placeholder="e.g. fulfilment:* or fulfilment:subscription:*"
        />
        <span className={styles.streamMeta}>
          {filteredStreams.length} of {streamsData.streams.length} streams
        </span>
      </div>

      <div className={styles.streamList}>
        {filteredStreams.map(stream => (
          <StreamChip
            key={stream.type}
            stream={stream}
            selected={stream.type === selectedStream}
            onSelect={handleStreamSelect}
          />
        ))}
        {filteredStreams.length === 0 && (
          <span className={styles.streamMeta}>No streams match filter</span>
        )}
      </div>

      {hasActiveFilter && (
        <div className={styles.searchRow}>
          <input
            type="text"
            className={styles.searchInput}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search entries..."
          />
          {searchLoading && <span className={styles.searchSummary}>Searching...</span>}
          {!searchLoading && searchResults && (
            <span className={styles.searchSummary}>
              Found {searchResults.entries.length}{" "}
              {searchResults.entries.length === 1 ? "match" : "matches"} in{" "}
              {searchResults.totalScanned.toLocaleString()} entries
              {searchResults.streamTypes.length > 1 &&
                ` across ${searchResults.streamTypes.length} streams`}
            </span>
          )}
        </div>
      )}

      {!hasActiveFilter && filteredStreams.length > 0 && (
        <EmptyState
          variant="empty"
          title="Select a stream"
          message="Click a stream above or type a filter pattern to view entries."
        />
      )}

      {entriesLoading && !searchQuery && hasActiveFilter && (
        <LoadingState message="Loading entries..." />
      )}

      {entriesError && !entriesLoading && !searchQuery && hasActiveFilter && (
        <EmptyState variant="error" title="Failed to load entries" message={entriesError.message} />
      )}

      {!entriesLoading && hasActiveFilter && displayedEntries.length === 0 && !searchQuery && (
        <EmptyState
          variant="empty"
          title="No entries"
          message="Selected streams have no entries."
        />
      )}

      {searchQuery && !searchLoading && searchResults && searchResults.entries.length === 0 && (
        <EmptyState
          variant="empty"
          title="No matches"
          message={`No entries matched "${searchQuery}" in ${searchResults.totalScanned.toLocaleString()} scanned entries.`}
        />
      )}

      {showEntries && (
        <div className={styles.entriesTable}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Time</th>
                <th>Type</th>
                <th>Source</th>
                <th>Subject</th>
                <th>Stream ID</th>
              </tr>
            </thead>
            <tbody>
              {displayedEntries.map(entry => (
                <EntryRow
                  key={entry.id}
                  entry={entry}
                  expanded={expandedEntryId === entry.id}
                  onToggle={() =>
                    setExpandedEntryId(expandedEntryId === entry.id ? null : entry.id)
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!consumersLoading &&
        consumersData?.groups &&
        consumersData.groups.length > 0 &&
        selectedStream && (
          <div className={styles.consumersSection}>
            <h3 className={styles.consumersTitle}>Consumer Groups</h3>
            {consumersData.groups.map(group => (
              <ConsumerGroupCard key={group.name} group={group} />
            ))}
          </div>
        )}
    </div>
  );
}

function StreamChip({
  stream,
  selected,
  onSelect,
}: {
  stream: NotificationStreamInfo;
  selected: boolean;
  onSelect: (type: string) => void;
}) {
  return (
    <button
      type="button"
      className={`${styles.streamChip} ${selected ? styles.streamChipSelected : ""}`}
      onClick={() => onSelect(stream.type)}
    >
      <span className={styles.streamChipType}>{stream.type}</span>
      <span className={styles.streamChipCount}>{stream.length}</span>
    </button>
  );
}

function EntryRow({
  entry,
  expanded,
  onToggle,
}: {
  entry: NotificationStreamEntry;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr className={styles.entryRow} onClick={onToggle}>
        <td className={styles.mono}>{formatTimestamp(entry.timestamp)}</td>
        <td className={styles.mono}>{entry.event?.type ?? "-"}</td>
        <td>
          {entry.event?.source ? (
            <Chip size="small" variant="filled">
              {entry.event.source}
            </Chip>
          ) : (
            "-"
          )}
        </td>
        <td className={styles.mono}>{entry.event?.subject ?? "-"}</td>
        <td className={styles.monoSmall}>{entry.id}</td>
      </tr>
      {expanded && entry.event && (
        <tr className={styles.expandedRow}>
          <td colSpan={5}>
            <pre className={styles.jsonPayload}>{JSON.stringify(entry.event.payload, null, 2)}</pre>
          </td>
        </tr>
      )}
    </>
  );
}

function ConsumerGroupCard({ group }: { group: NotificationStreamConsumerGroup }) {
  return (
    <div className={styles.consumerGroupCard}>
      <div className={styles.consumerGroupHeader}>
        <span className={styles.consumerGroupName}>{group.name}</span>
        <span className={styles.consumerGroupPending}>{group.pending} pending</span>
      </div>
      {group.consumers.length > 0 && (
        <div className={styles.consumerList}>
          {group.consumers.map(consumer => (
            <div key={consumer.name} className={styles.consumerItem}>
              <span className={styles.monoSmall}>{consumer.name}</span>
              <span className={styles.consumerStats}>
                {consumer.pending} pending · idle {formatIdleTime(consumer.idle)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default NotificationEventLog;
